import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Wallet, KanbanSquare, LayoutDashboard, Shield, Check } from 'lucide-react';

const VIEWS = [
  { id: 'calendar_hero', icon: Calendar, title: 'Calendar Hero', desc: 'Tu calendario al centro del escenario.' },
  { id: 'financial_hub', icon: Wallet, title: 'Financial Hub', desc: 'Balance y finanzas primero.' },
  { id: 'project_board', icon: KanbanSquare, title: 'Project Board', desc: 'Kanban de proyectos como inicio.' },
  { id: 'daily_digest', icon: LayoutDashboard, title: 'Daily Digest', desc: 'Resumen diario de todo.' },
];

export default function Onboarding() {
  const { user, updateProfile, reload, loading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('calendar_hero');
  const [step, setStep] = useState(0);

  if (loading) return null;
  if (!user) { navigate('/login'); return null; }

  const finish = async () => {
    await updateProfile({ entry_view: view });
    await api.post('/auth/onboard-complete');
    await reload();
    toast.success('¡Todo listo!');
    navigate('/plannerapp');
  };

  return (
    <div className="min-h-screen grid place-items-center pp-grad-hero px-4 py-10" style={{ background: '#07090A' }}>
      <div className="dark w-full max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="pp-glass p-8">
          {step === 0 ? (
            <>
              <Shield className="pp-mint mb-4" size={32} />
              <h1 className="text-2xl font-bold text-white mb-3">Tu privacidad, primero</h1>
              <p className="text-sm text-[#A6C3B4] leading-relaxed mb-6">
                Tus datos te pertenecen exclusivamente a ti. PlannerPRO utiliza cifrado AES-256 y arquitectura zero-knowledge en tu bóveda privada: ni nuestro equipo técnico puede acceder a esa información. Nunca vendemos ni compartimos tus datos. Puedes exportar o eliminar toda tu información en cualquier momento.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {['AES-256', 'TLS 1.3', 'Zero-Knowledge', 'GDPR + CCPA'].map((b) => (
                  <span key={b} className="text-xs px-3 py-1 rounded-full border border-[rgba(168,230,206,0.2)] pp-mint">{b}</span>
                ))}
              </div>
              <button data-testid="onboard-accept" onClick={() => setStep(1)} className="w-full h-12 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Acepto y continuar</button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white mb-2">Elige tu vista de entrada</h1>
              <p className="text-sm text-[#A6C3B4] mb-6">Puedes cambiarla luego en Ajustes.</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {VIEWS.map((v) => (
                  <button key={v.id} data-testid={`onboard-view-${v.id}`} onClick={() => setView(v.id)} className={`relative text-left p-4 rounded-xl border transition-colors ${view === v.id ? 'border-[#3EB489] pp-glow-mint bg-[rgba(62,180,137,0.08)]' : 'border-[rgba(168,230,206,0.14)] hover:bg-[rgba(255,255,255,0.04)]'}`}>
                    {view === v.id && <Check size={16} className="absolute top-3 right-3 pp-mint" />}
                    <v.icon className="pp-mint mb-2" size={22} />
                    <div className="font-semibold text-white">{v.title}</div>
                    <div className="text-xs text-[#A6C3B4] mt-1">{v.desc}</div>
                  </button>
                ))}
              </div>
              <button data-testid="onboard-finish" onClick={finish} className="w-full h-12 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Entrar a PlannerPRO</button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
